import React, {
	type RefObject,
	type ReactNode,
	type CSSProperties,
	useEffect,
	useRef,
	useState,
} from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';

import { ReactRenderer } from '@atlaskit/renderer';

import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMediaFeatureFlags } from '@confluence/fabric-media-support/entry-points/mediaFeatureFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ANALYTICS_OPERATIONAL_SUBJECT, ANALYTICS_ORIGIN } from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import type { Snippet } from '../types/snippet.ts';
import type { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';
import { wrapAdfWithDocNode } from '../utils/adfUtils.ts';

import { getProviders } from './providers.ts';

const styles = cssMap({
	scaledContainer: {
		overflow: 'hidden',
		userSelect: 'none',
		pointerEvents: 'none',
	},
	scaledContent: {
		height: 'auto',
		transformOrigin: 'top left',
	},
	loadingWrapper: {
		width: '100%',
		height: '100%',
	},
});

/** Desired output dimensions (container size) */
export type Dimensions = Pick<CSSProperties, 'width' | 'maxHeight'>;

export const SnippetPreview = ({
	snippet,
	dimensions,
	/** Width in px at which content renders before scaling */
	renderWidth,
	loadingComponent,
}: {
	snippet: Snippet;
	dimensions: Dimensions;
	renderWidth: number;
	loadingComponent?: ReactNode;
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const showFallback = !!loadingComponent && isLoading;

	return (
		<>
			{showFallback && (
				<Flex xcss={styles.loadingWrapper} alignItems="center" justifyContent="center">
					{loadingComponent}
				</Flex>
			)}
			<ScaledContainer dimensions={dimensions} renderWidth={renderWidth} shouldHide={showFallback}>
				<Renderer
					adf={snippet.body}
					snippetId={snippet.snippetId}
					scope={snippet.scope}
					onRendered={() => setIsLoading(false)}
				/>
			</ScaledContainer>
		</>
	);
};

const Renderer = ({
	adf,
	snippetId,
	scope,
	onRendered,
}: {
	adf: any;
	snippetId: string;
	scope?: SnippetScope;
	onRendered: () => void;
}) => {
	const { cloudId, userId, featureFlags } = useSessionData();
	const { fireFailedAnalytic } = useSnippetAnalytics(ANALYTICS_ORIGIN.SNIPPET_PANEL);

	const handleRenderError = (error: any) => {
		const reasonString = error instanceof Error ? error.message : JSON.stringify(error);
		const errorToLog = new Error(`Error when rendering snippet preview: ${reasonString}`);
		getMonitoringClient().submitError(errorToLog, {
			attribution: Attribution.INTEGRATIONS,
		});
		fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_PREVIEW_RENDER, {});
	};

	const providers = getProviders(cloudId, userId, snippetId, scope);

	return (
		<ReactRenderer
			adfStage="stage0"
			document={wrapAdfWithDocNode(adf)}
			appearance="full-page"
			allowColumnSorting={false}
			enableSsrInlineScripts={false}
			allowHeadingAnchorLinks={false}
			allowCustomPanels={fg('platform_synced_block_patch_13')}
			stickyHeaders={undefined}
			dataProviders={providers}
			onComplete={onRendered}
			onError={handleRenderError}
			allowPlaceholderText
			media={{
				featureFlags: getMediaFeatureFlags(featureFlags),
			}}
		/>
	);
};

const useContainerWidth = (containerRef: RefObject<HTMLDivElement | null>) => {
	const [containerWidth, setContainerWidth] = useState<number | null>(null);

	useEffect(() => {
		const node = containerRef.current;
		if (!node || typeof ResizeObserver === 'undefined') {
			return;
		}

		const observer = new ResizeObserver(([entry]) => {
			const width = entry.contentRect.width;
			if (width > 0) {
				setContainerWidth(width);
			}
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, [containerRef]);

	return containerWidth;
};

const ScaledContainer = ({
	children,
	dimensions,
	renderWidth,
	shouldHide = false,
}: {
	children: React.ReactNode;
	dimensions: Dimensions;
	renderWidth: number;
	shouldHide?: boolean;
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const containerWidth = useContainerWidth(containerRef);

	const scaleFactor = containerWidth !== null ? containerWidth / renderWidth : 0;
	const isMeasured = containerWidth !== null;

	return (
		<Box
			ref={(node: HTMLDivElement | null) => {
				containerRef.current = node;
				node?.setAttribute('inert', '');
			}}
			xcss={styles.scaledContainer}
			testId="snippet-preview-container"
			style={{
				width: dimensions.width,
				maxHeight: dimensions.maxHeight,
				display: shouldHide ? 'none' : 'block',
			}}
		>
			<Box
				xcss={styles.scaledContent}
				style={{
					transform: `scale(${scaleFactor})`,
					width: renderWidth,
					visibility: isMeasured ? 'visible' : 'hidden',
					containerType: 'inline-size' as CSSProperties['containerType'],
				}}
			>
				{children}
			</Box>
		</Box>
	);
};
